import { useEffect, useState } from "react";
import { FilterSection } from "../../../data-model/filterValues";
import { FilterCreationConfig } from "./filterCreationConfig";
import {
  areFilterDependenciesReady,
  buildFilterData,
} from "./filterDataBuilder";
import { useFilterDataSelectors } from "./useFilterDataSelectors";

/**
 * Custom hook that manages all data dependencies and coordinates filter data building.
 * Combines Redux selectors (including `estimateOptions` for exception-flow orgs),
 * readiness checks, and filter construction into a single hook.
 *
 * Effect dependencies use a stable key derived from config section ids so callers
 * may pass a freshly allocated config array each render (e.g. `getFilterCreationConfig(orgId)`)
 * without retriggering `setFilterData` when the filter shape is unchanged.
 *
 * @param filterCreationConfig - Array of FilterCreationConfig defining which filters to build
 * @returns Object containing:
 *   - filterData: Built FilterSection array for the filter panel
 *   - isPostgresFacetResultsLoading: Whether Postgres facet counts are still loading
 *   - isDataReady: Whether all dependencies are ready for filter building
 */
export const useFilterDataDependencies = (
  filterCreationConfig: FilterCreationConfig[],
) => {
  const {
    postgresFacetResults,
    isPostgresFacetResultsLoading,
    authStatusOptions,
    boOptions,
    estimateOptions,
    providerDetails,
    allProvidersDetails,
  } = useFilterDataSelectors();

  const [filterData, setFilterData] = useState<FilterSection[]>([]);

  const filterConfigKey = filterCreationConfig
    .map((config) => config.id)
    .join("|");

  // Build filter data when dependencies are ready
  useEffect(() => {
    if (
      areFilterDependenciesReady({
        postgresFacetResults: postgresFacetResults ?? {
          facet_results: {},
          total_count: 0,
        },
        authStatusOptions,
        boOptions,
        estimateOptions,
        providerDetails,
        filterCreationConfig: filterCreationConfig,
      })
    ) {
      const newFilterData = buildFilterData({
        postgresFacetResults: postgresFacetResults ?? {
          facet_results: {},
          total_count: 0,
        },
        authStatusOptions: authStatusOptions ?? [],
        boOptions: boOptions ?? [],
        estimateOptions: estimateOptions ?? [],
        allProvidersDetails: allProvidersDetails ?? [],
        filterCreationConfig: filterCreationConfig,
      });
      setFilterData(newFilterData);
    }
  }, [
    isPostgresFacetResultsLoading,
    authStatusOptions,
    boOptions,
    estimateOptions,
    providerDetails,
    allProvidersDetails,
    postgresFacetResults,
    filterConfigKey,
  ]);

  return {
    filterData,
    isPostgresFacetResultsLoading,
    isDataReady: areFilterDependenciesReady({
      postgresFacetResults: postgresFacetResults ?? {
        facet_results: {},
        total_count: 0,
      },
      authStatusOptions,
      boOptions,
      estimateOptions,
      providerDetails,
      filterCreationConfig: filterCreationConfig,
    }),
  };
};
